import type { ChangeEvent } from 'react';
import { useCallback } from 'react';

import { Button, Input, Spinner } from 'design-system';
import { Link } from 'lucide-react';

import useShareButton from '@/features/_shared/ShareButton/useShareButton';
import ShareDialog from '@/features/_shared/ShareDialog';
import { useGenerateShareLink } from '@/features/dashboard/api/mutations';
import {
  SHARE_DASHBOARD_EMAIL_TEMPLATE,
  SHARED_DASHBOARD_PATH
} from '@/features/dashboard/constants/const';
import copyToClipboard from '@/shared/lib/copyToClipboard';

const GenerateLinkSection = ({ dashboardName }: { dashboardName: string }) => {
  const { data: generatedUrl, isPending, mutate } = useGenerateShareLink(dashboardName);

  return (
    <section className='mt-6'>
      <h3 className='mb-2 flex items-center gap-2 text-sm font-medium'>
        <Link
          aria-hidden='true'
          className='size-4'
        />
        Share a link
      </h3>
      <div className='flex items-center gap-2'>
        <Input
          readOnly
          aria-label='Generated dashboard link'
          autoComplete='off'
          className='grow truncate'
          placeholder='Link will appear here...'
          value={generatedUrl ?? ''}
        />
        {generatedUrl ? (
          <Button
            className='h-12 w-[120px]'
            onClick={() => copyToClipboard(generatedUrl, 'Link copied to clipboard!')}
          >
            Copy link
          </Button>
        ) : (
          <Button
            className='h-12 w-[120px]'
            disabled={isPending}
            onClick={() => {
              mutate();
            }}
          >
            {isPending ? (
              <>
                <Spinner className='size-4' />
                <span className='sr-only'>Generating link</span>
              </>
            ) : (
              'Generate link'
            )}
          </Button>
        )}
      </div>
    </section>
  );
};

export const DashboardShareDialog = ({
  open,
  title,
  onClose
}: {
  open: boolean;
  title: string;
  onClose: () => void;
}) => {
  const shareUrl = `${window.location.origin}${SHARED_DASHBOARD_PATH}`;

  const { content, setContent, recipient, handleChangeRecipient, sendEmail, emailError } =
    useShareButton(undefined, shareUrl, {
      dashboardName: title,
      template: SHARE_DASHBOARD_EMAIL_TEMPLATE
    });

  const handleSendEmail = useCallback(() => {
    if (sendEmail()) onClose();
  }, [sendEmail, onClose]);

  const handleContentChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setContent(e.target.value);
    },
    [setContent]
  );

  return (
    <ShareDialog
      content={content}
      emailError={emailError}
      handleChangeRecipient={handleChangeRecipient}
      header={<h2 className='font-heading text-xl font-semibold'>Share {title}</h2>}
      open={open}
      recipient={recipient}
      renderLinkSection={<GenerateLinkSection dashboardName={title} />}
      shareUrl={shareUrl}
      onClose={onClose}
      onContentChange={handleContentChange}
      onSendEmail={handleSendEmail}
    />
  );
};
